/* 手机端底部固定购买条
   - 件数 / 合计：订阅主题 xo-cart-form store，与主购买区同一 key（sectionId/productId）
   - 加购 / 结账：由 snippet 内的 xo-cart-add 负责，本组件不直接调用 cart API
   - 显示：仅 767.98px 以下，且规格区顶部（.xo-product-info-content__variant）进入屏幕底部后滑入，
     滚回其上方时滑出；隐藏时 inert。无规格区锚点时一直显示 */
class MobileStickyBuyBar extends HTMLElement {
  constructor() {
    super();
    this.isActive = null;
    this.variants = null;
    this.unsubscribe = null;
    this.mediaQuery = window.matchMedia('(max-width: 767.98px)');
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleStoreReady = this.handleStoreReady.bind(this);
    this.handleExtrasSummary = this.handleExtrasSummary.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.syncLoading = this.syncLoading.bind(this);
    this.extras = null;
    this.loadingObserver = null;
    this.anchorObserver = null;
    this.anchorReached = true;
  }

  connectedCallback() {
    this.productEl = this.closest('xo-product');
    if (!this.productEl) return;

    this.countEl = this.querySelector('[data-sticky-buy-bar-count]');
    this.totalEl = this.querySelector('[data-sticky-buy-bar-total]');
    this.discountRow = this.querySelector('[data-sticky-buy-bar-discount-row]');
    this.discountEl = this.querySelector('[data-sticky-buy-bar-discount]');
    // キット / よく一緒に 的整单汇总（assets/product-extras-core.js 派发）
    this.productEl.addEventListener('product-extras:summary', this.handleExtrasSummary);
    this.storeKey = `${this.dataset.sectionId}/${this.dataset.productId}`;

    this.mediaQuery.addEventListener('change', this.handleMediaChange);
    this.observeAnchor();
    this.updateVisibility();

    // 加载圆点：只在被点击的按钮上显示（主题对同商品所有 xo-cart-add 同时加 xo-loading）
    // 用捕获阶段：xo-cart-add 自身的 click 会同步加 xo-loading，冒泡时 syncLoading 会先于 data-active-loading 执行，圆点永远不显示
    this.addEventListener('click', this.handleClick, true);
    this.loadingObserver = new MutationObserver(this.syncLoading);
    this.loadingObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['xo-loading'],
    });

    if (typeof window.cartFormSubscribe === 'function') {
      this.subscribe();
    } else {
      window.addEventListener('load', this.handleStoreReady, { once: true });
    }
  }

  disconnectedCallback() {
    if (this.productEl) this.productEl.removeEventListener('product-extras:summary', this.handleExtrasSummary);
    if (typeof this.unsubscribe === 'function') this.unsubscribe();
    this.unsubscribe = null;
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    this.removeEventListener('click', this.handleClick, true);
    if (this.loadingObserver) this.loadingObserver.disconnect();
    this.loadingObserver = null;
    if (this.anchorObserver) this.anchorObserver.disconnect();
    this.anchorObserver = null;
    window.removeEventListener('load', this.handleStoreReady);
    document.documentElement.classList.remove('has-mobile-sticky-buy-bar', 'has-sticky-buy-bar-discount');
    this.isActive = null;
  }

  handleStoreReady() {
    if (this.isConnected) this.subscribe();
  }

  subscribe() {
    if (typeof window.cartFormSubscribe !== 'function') return;
    this.unsubscribe = window.cartFormSubscribe(this.handleStateChange);
    this.handleStateChange();
  }

  handleExtrasSummary(event) {
    this.extras = event.detail || null;
    this.handleStateChange();
  }

  handleStateChange() {
    // 选了「よく一緒に購入される商品」或已达折扣档位（個数 ≥ 2）时显示整单汇总（含割引），否则保持原逻辑
    if (this.extras && (this.extras.addonCount > 0 || this.extras.discount > 0)) {
      this.setText(this.countEl, String(this.extras.count));
      this.setText(this.totalEl, this.formatMoney(this.extras.total));
      if (this.discountRow) this.discountRow.hidden = this.extras.discount <= 0;
      document.documentElement.classList.toggle('has-sticky-buy-bar-discount', this.extras.discount > 0);
      this.setText(this.discountEl, `-${this.formatMoney(this.extras.discount)}`);
      return;
    }
    if (this.discountRow) this.discountRow.hidden = true;
    document.documentElement.classList.remove('has-sticky-buy-bar-discount');

    const state = typeof window.getCartFormState === 'function' ? window.getCartFormState() : null;
    const formState = state ? state[this.storeKey] : null;
    const quantity = Math.max(1, parseInt(formState && formState.quantity, 10) || 1);
    const variant = this.getVariant(formState && formState.variantSelected);

    this.setText(this.countEl, String(quantity));
    if (variant) this.setText(this.totalEl, this.formatMoney(variant.price * quantity));
  }

  setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  getVariants() {
    if (this.variants) return this.variants;

    const template = this.productEl.querySelector(':scope > xo-product-data template');
    const script = template ? template.content.querySelector('script') : null;
    try {
      this.variants = JSON.parse(script ? script.textContent : '{}').variants || [];
    } catch (error) {
      this.variants = [];
    }
    return this.variants;
  }

  // 与主题 getVariantId 规则一致；复制后排序，不改动变体数据
  getVariant(variantSelected) {
    const variants = this.getVariants();
    if (variants.length === 1) return variants[0];

    if (variantSelected) {
      const selected = Object.values(variantSelected).filter(Boolean).map(String).sort();
      const matched = variants.find((variant) => {
        const options = (variant.options || []).map(String).sort();
        if (options.length === selected.length) {
          return options.every((option, index) => option === selected[index]);
        }
        return options.toString().includes(selected.toString());
      });
      if (matched) return matched;
    }

    const fallbackId = Number(this.dataset.variantId);
    return variants.find((variant) => variant.id === fallbackId) || null;
  }

  // 与主题 formatMoney 占位符规则一致；小数位保留 toFixed 结果，与 Liquid money 输出一致
  formatMoney(cents) {
    const rawFormat = document.documentElement.getAttribute('xo-money-format') || '{{amount}}';
    const format = rawFormat.replace(/<[^>]*>/g, '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    const rules = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
    };
    const rule = rules[match[1]];
    if (!rule) return format;

    const [precision, thousands, decimal] = rule;
    const [integer, fraction] = (Number(cents) / 100).toFixed(precision).split('.');
    const value = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    return format.replace(placeholder, value);
  }

  handleClick(event) {
    const button = event.target.closest('.mobile-sticky-buy-bar__button');
    if (!button || button.disabled) return;
    const adder = button.closest('xo-cart-add');
    if (!adder || adder.hasAttribute('xo-disabled')) return;
    this.querySelectorAll('xo-cart-add[data-active-loading]').forEach((el) => {
      if (el !== adder) el.removeAttribute('data-active-loading');
    });
    adder.setAttribute('data-active-loading', 'true');
  }

  // xo-loading 变化 / 按钮被 xo-product-will-change 替换后同步加载态
  syncLoading() {
    this.querySelectorAll('xo-cart-add').forEach((adder) => {
      const button = adder.querySelector('.mobile-sticky-buy-bar__button');
      const loading = adder.hasAttribute('xo-loading') && adder.hasAttribute('data-active-loading');
      if (button) {
        button.classList.toggle('is-loading', loading);
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
      }
      if (!adder.hasAttribute('xo-loading') && adder.hasAttribute('data-active-loading')) {
        adder.removeAttribute('data-active-loading');
      }
    });
  }

  handleMediaChange() {
    this.updateVisibility();
  }

  // 锚点顶部在视口底边之上（进入屏幕或已滚过）即视为到达；进出视口底边 / 顶边时 IO 都会回调
  observeAnchor() {
    const anchor = this.productEl.querySelector('.xo-product-info-content__variant');
    if (!anchor || !('IntersectionObserver' in window)) return;
    this.anchorReached = anchor.getBoundingClientRect().top < window.innerHeight;
    this.anchorObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.anchorReached = entry.boundingClientRect.top < window.innerHeight;
      this.updateVisibility();
    });
    this.anchorObserver.observe(anchor);
  }

  updateVisibility() {
    this.setVisible(this.mediaQuery.matches && this.anchorReached);
  }

  setVisible(visible) {
    if (visible === this.isActive) return;
    this.isActive = visible;
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    document.documentElement.classList.toggle('has-mobile-sticky-buy-bar', visible);
  }
}

if (!customElements.get('mobile-sticky-buy-bar')) {
  customElements.define('mobile-sticky-buy-bar', MobileStickyBuyBar);
}
